/**
 * Rewrite absolute MinIO / object-store URLs to a same-origin `/school-media/...` path
 * (CRA proxies this in dev; production can reverse-proxy the same path).
 * Keeps signed query params intact so AWS4 signatures still validate against MinIO.
 */
export function sameOriginSchoolMediaUrl(url: string): string | null {
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    if (!parsed.pathname.startsWith('/school-media/')) return null;
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

/** Prefer same-origin media URL when the path is the public `school-media` bucket. */
export function preferSameOriginSchoolMediaUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  return sameOriginSchoolMediaUrl(trimmed) || trimmed;
}

const JPEG_SOI = [0xff, 0xd8, 0xff];
const PNG_SIG = [0x89, 0x50, 0x4e, 0x47];
const WEBP_RIFF = [0x52, 0x49, 0x46, 0x46];
const GIF_SIG = [0x47, 0x49, 0x46];

/** True when the first bytes look like a real image (not HTML/XML/error text). */
export function blobLooksLikeImage(bytes: ArrayBuffer): boolean {
  const u8 = new Uint8Array(bytes);
  if (u8.length < 3) return false;
  const startsWith = (sig: number[]) => sig.every((b, i) => u8[i] === b);
  if (startsWith(JPEG_SOI) || startsWith(PNG_SIG) || startsWith(GIF_SIG)) return true;
  if (u8.length >= 12 && startsWith(WEBP_RIFF)) {
    // RIFF....WEBP
    return u8[8] === 0x57 && u8[9] === 0x45 && u8[10] === 0x42 && u8[11] === 0x50;
  }
  return false;
}
